'use client'

import React, { FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { segment } from '@/utils/tracking.utils'
import LoginContext from './LoginContext'
import useCartActions from '@/hooks/useCartActions'
import useAuthErrorUrlParam from '@/hooks/useAuthErrorUrlParam'
import { useOurStore } from '@/app/_providers/StoreProvider'
import TXT from '@/messages'
import { IS_PRODUCTION_DEPLOYMENT } from '@/config/environments.config'
import { cartExists } from '@/utils/cart.utils'
import AuthMenu from '@/app/_components/AuthMenu/AuthMenu'
import Modal from '@/app/_components/Modal/Modal'
import MobileAuthWidget from '@/app/_components/MobileAuthWidget/MobileAuthWidget'
// useUser is allowed in this file only
// eslint-disable-next-line no-restricted-imports
import useUser from '@/hooks/useUser'
import { logoutAction } from '@/app/_actions/authentication.actions'
import { getInternalRedirectPathname, getLoginRoute } from '@/utils/urls.utils'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'
import { removeGuestCartId } from '@/utils/store.utils'
import { getSiteOrigin } from '@/utils/site.utils'
import { handleLogoutRedirect } from '@/utils/providers/LoginProvider.utils'
import { setSentryUser } from '@/utils/sentry.utils'
import { useOptionalSitePath } from '@/app/_providers/SellerProvider/SellerProvider'

const LoginProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const sitePath = useOptionalSitePath() ?? '/'
  const resetAll = useOurStore((state) => state.resetAll)
  const syncFormValuesWithProfile = useOurStore((state) => state.syncFormValuesWithProfile)
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
  const { cart } = useCart()
  const { user, membershipMeta, membership, membershipDetails, loading } = useUser()
  useAuthErrorUrlParam()

  const handleLogin = useCallback((): void => {
    window.location.href = getLoginRoute(new URL(window.location.pathname, getSiteOrigin()).toString())
  }, [])

  useEffect(() => {
    if (!loading) {
      setSentryUser(user)
    }
  }, [user, loading])

  // Check user whenever we update the route
  // use set timeout to prevent the callback from firing too often
  useEffect(() => {
    if (user) {
      syncFormValuesWithProfile(user.profile)

      // Identify the user in segment
      const {
        firstName,
        lastName,
        fullName: name,
        email,
        personId,
      } = user.profile

      const traits = {
        id: user.profile.puid,
        email,
        name,
        firstName,
        lastName,
      }

      // we should always have a personId, but if we don't fall back to traits only
      if (personId) {
        segment.identify(personId, traits)
      } else {
        segment.identify(traits)
      }
    }
  }, [user, syncFormValuesWithProfile])

  const { deleteCart } = useCartActions({ sellerId: cart?.sellerId })

  const handleDeleteCartAndLogout = useCallback(async (logUserOut = false) => {
    removeGuestCartId()
    // delete cart
    if (cart && cartExists(cart?.id)) {
      await deleteCart()
    }

    if (logUserOut) {
      // reset ourStore
      resetAll()
      if (user) {
        // NOTE: redirect happens on the client to avoid CORS issues
        handleLogoutRedirect(await logoutAction(getInternalRedirectPathname(sitePath)))
      }
      // call logout action
    }
  }, [cart, deleteCart, user, resetAll, sitePath])

  const providerValue = useMemo(() => ({
    user,
    membership,
    membershipMeta,
    membershipDetails,
    isLogoutModalOpen,
    setIsLogoutModalOpen,
    handleDeleteCartAndLogout,
    handleLogin,
    loading,
  }), [
    user,
    membership,
    membershipMeta,
    membershipDetails,
    isLogoutModalOpen,
    setIsLogoutModalOpen,
    handleDeleteCartAndLogout,
    loading,
    handleLogin,
  ])

  return (
    <LoginContext.Provider value={providerValue}>
      {/* Header Menu */}
      <AuthMenu />
      {children}
      {/* Logout modal */}
      <Modal
        body={(
          <>
            {TXT.confirmationModals.logout.bodyLines.map((line) => <div key={line}>{line}</div>)}
          </>
        )}
        confirmButton={TXT.confirmationModals.logout.confirmButton}
        cancelButton={TXT.confirmationModals.logout.cancelButton}
        visible={isLogoutModalOpen}
        setVisible={(visible) => { setIsLogoutModalOpen(visible) }}
        onConfirm={() => handleDeleteCartAndLogout(true)}
      />
      <MobileAuthWidget
        incomingUser={user}
        isProd={IS_PRODUCTION_DEPLOYMENT}
      />
    </LoginContext.Provider>
  )
}

export default LoginProvider
